/**
 * validateMapLocal.js
 * Local-only validation — now accepts dynamic rules from backend for better sync.
 */

function issue(ruleId, ruleType, severity, locationType, locationId, message, why, fix) {
    return {
        issue_id: `local_${ruleId}_${locationId || "map"}_${Math.random().toString(36).slice(2, 9)}`,
        rule_id: ruleId,
        rule_type: ruleType,
        severity,
        location_type: locationType,
        location_id: locationId || "",
        location_label: locationId || "map",
        message,
        why_it_matters: why,
        suggested_fix: fix,
        status: "open",
    };
}

function countBySeverity(issues) {
    const c = { critical: 0, major: 0, minor: 0, informational: 0 };
    for (const i of issues) {
        if (c[i.severity] !== undefined) c[i.severity] += 1;
    }
    return c;
}

function statusFromIssues(issues) {
    const c = countBySeverity(issues);
    if (c.critical > 0 || c.major > 0 || c.blocker > 0 || c.error > 0) return "fail";
    if (c.minor > 0 || c.warning > 0) return "pass_with_warnings";
    return "pass";
}

/**
 * @param {string} rawJsonText
 * @param {Array} architecturalRules
 * @param {Array} deterministicRules
 * @returns {object} validation result shape
 */
export function validateMapLocal(rawJsonText, architecturalRules = [], deterministicRules = []) {
    const issues = [];
    const getRule = (id, type) => {
        const catalog = type === "architecture" ? architecturalRules : deterministicRules;
        return catalog.find(r => r.rule_id === id) || { rule_id: id, rule_name: id, why_it_matters: "", fix_suggestion: "" };
    };

    let map;
    try {
        map = JSON.parse(rawJsonText);
    } catch (e) {
        issues.push(issue("PARSE", "deterministic", "critical", "field", "body", "Invalid JSON.", "Requires well-formed JSON.", "Fix syntax."));
        return buildResult(issues, map);
    }

    if (!map || typeof map !== "object") {
        issues.push(issue("DR-MAP", "deterministic", "critical", "section", "", "Map root must be object.", "", ""));
        return buildResult(issues, map);
    }

    const nodes = Array.isArray(map.nodes) || (map.map_definition && Array.isArray(map.map_definition.nodes))
        ? (map.nodes || map.map_definition.nodes)
        : null;

    if (!nodes || nodes.length === 0) {
        const r = getRule("DR-01", "deterministic");
        issues.push(issue(r.rule_id, "deterministic", r.severity || "critical", "section", "nodes", r.description || "Nodes required.", r.why_it_matters, r.fix_suggestion));
        return buildResult(issues, map);
    }

    // DET-001 Sync
    const hasEntry = (map.map_definition?.entry_node) || nodes.some(n => n.node_type === "entry");
    if (!hasEntry) {
        const r = getRule("DET-001", "deterministic");
        issues.push(issue(r.rule_id, "deterministic", r.severity || "blocker", "node", "", r.rule_name, r.why_it_matters, r.fix_suggestion));
    }

    // AR Layers Sync
    const layers = new Set(nodes.filter(Boolean).map((n) => (n.layer || "").toLowerCase()));
    ["intent", "meaning", "pattern", "behavior"].forEach((L, idx) => {
        if (!layers.has(L)) {
            const r = getRule(`AR-0${idx + 1}`, "architecture");
            issues.push(issue(r.rule_id, "architecture", r.severity || "critical", "flow", L, r.rule_name, r.why_it_matters, r.fix_suggestion));
        }
    });

    const detIssues = issues.filter((i) => i.rule_type === "deterministic");
    const archIssues = issues.filter((i) => i.rule_type === "architecture");
    const detStatus = statusFromIssues(detIssues);
    const archStatus = statusFromIssues(archIssues);

    const overall = (detStatus === "fail" || archStatus === "fail") ? "fail" : "pass";
    const counts = countBySeverity(issues);

    return {
        validation_run_id: `local_${Date.now()}`,
        map_id: map.map_id || "",
        version: map.version || "",
        overall_status: overall,
        deterministic_status: detStatus,
        architecture_status: archStatus,
        issues,
        ...counts,
        created_at: new Date().toISOString(),
        _localOnly: true,
    };
}

function buildResult(issues, map) {
    const counts = countBySeverity(issues);
    return {
        validation_run_id: `local_${Date.now()}`,
        map_id: map?.map_id || "",
        version: map?.version || "",
        overall_status: "fail",
        issues,
        ...counts,
        created_at: new Date().toISOString(),
        _localOnly: true,
    };
}

export const SAMPLE_MAP_JSON = `{
  "map_id": "MAP-LOGIN-001",
  "map_name": "User Login Authentication Flow",
  "project_name": "Code Assistant Auth System",
  "flow_metadata": {
    "flow_id": "FLOW-LOGIN-001",
    "flow_name": "User Login Flow",
    "version": "1.0",
    "status": "approved",
    "domain": "authentication",
    "technical_specification": "FastAPI backend implementation"
  },
  "variable_contract": {
    "required_variables": ["email", "password"],
    "derived_variables": ["is_credential_matched"],
    "variable_definitions": {
      "email": { "type": "string", "source": "user_input", "missing_state_check": "block_with_error" },
      "password": { "type": "string", "source": "user_input", "missing_state_check": "block_with_error" },
      "is_credential_matched": { "type": "boolean", "source": "meaning_layer" }
    }
  },
  "intent_contract": {
    "intent_id": "intent_login",
    "intent_type": "qualification",
    "completion_rule": "all_required_variables_present"
  },
  "validator_contract": { "must_validate_schema": true },
  "intent_definition": {
    "intent_id": "intent_login",
    "intent_type": "qualification",
    "flow_mode": "hybrid"
  },
  "meaning_contract": {
    "entities": { "user": "end-user", "session": "active-token" },
    "normalization_rules": ["lowercase_email", "trim_whitespace"]
  },
  "question_contract": {
    "questions": {
      "email": {
        "question_id": "Q-01",
        "question_text": "Please enter your email",
        "question_type": "qualifying"
      },
      "password": {
        "question_id": "Q-02",
        "question_text": "Please enter your password",
        "question_type": "qualifying"
      }
    },
    "next_question_priority": ["email", "password"]
  },
  "decision_policy": {
    "intent_id": "intent_login",
    "flow_mode": "hybrid",
    "terminal_outcomes": ["login_success", "login_failed"],
    "final_outcome_blocked_until": ["email", "password"]
  },
  "map_definition": {
    "entry_node": "N-01",
    "nodes": [
      {
        "node_id": "N-01",
        "node_type": "entry",
        "layer": "intent",
        "intent_id": "intent_login",
        "purpose": "Initialize login flow",
        "branches": [{ "condition": "default", "destination_node_id": "N-VAL" }]
      },
      {
        "node_id": "N-VAL",
        "node_type": "validation",
        "layer": "meaning",
        "intent_id": "intent_login",
        "meaning_id": "email",
        "pattern_id": "Q-01",
        "purpose": "Validate credentials",
        "branches": [
          { "condition": "inputs_valid", "destination_node_id": "N-02", "priority": 1 },
          { "condition": "inputs_invalid", "destination_node_id": "N-04", "priority": 2 }
        ]
      },
      {
        "node_id": "N-02",
        "node_type": "decision",
        "layer": "pattern",
        "intent_id": "intent_login",
        "purpose": "Evaluate credential match",
        "condition": "is_credential_matched",
        "branches": [
          { "condition": "true", "destination_node_id": "N-03", "priority": 1 },
          { "condition": "false", "destination_node_id": "N-04", "priority": 2 },
          { "condition": "else", "destination_node_id": "N-04", "priority": 3 }
        ]
      },
      {
        "node_id": "N-03",
        "node_type": "terminal",
        "layer": "behavior",
        "intent_id": "intent_login",
        "behavior_id": "login_success",
        "purpose": "Terminal success node",
        "output_type": "login_success"
      },
      {
        "node_id": "N-04",
        "node_type": "terminal",
        "layer": "behavior",
        "intent_id": "intent_login",
        "behavior_id": "login_failed",
        "purpose": "Terminal failure node",
        "output_type": "login_failed"
      }
    ]
  },
  "outcome_contract": {
    "terminal_outcomes": ["login_success", "login_failed"],
    "outcome_definitions": {
      "login_success": { "outcome_type": "direct_answer", "allowed_when": ["is_credential_matched == true"] },
      "login_failed": { "outcome_type": "route", "allowed_when": ["is_credential_matched == false"] }
    }
  },
  "trace_contract": {
    "trace_fields": [
      "intent_id", "intent_type", "flow_mode", "known_variables", 
      "missing_required_variables", "decision_action", "decision_reason", 
      "next_question_variable", "final_outcome_blocked"
    ]
  }
}`;
